import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ThemeName } from '@ps-int/general-themes';
import formatDate from '@ps-int/ufo-helpers/lib/date/date-to-string';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { Time } from '../../../../../../common/const/time';
import Background from '../../../../shared/components/background';
import CoverLayer from '../../../../shared/components/cover-layer';
import { getThemeName, isCoverLayerEnabled } from '../../../../shared/features/config/selectors';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { i18n, i18nMonthsGenitive } from '../../../../shared/helpers/i18n';
import { getPageClassName } from '../../../../shared/helpers/theme';
import { BroadcastStatus } from '../../../../types';
import { getBroadcastLiveData, getBroadcastViewStatus } from '../../features/broadcast/selectors';
import BackgroundAudioButton from '../background-audio-button';
import Countdown from '../countdown';
import classes from './index.css';

enum PlannedBroadcastStatus {
    DATE = 'date',
    HOUR = 'hour',
    NOW = 'now',
}

function computePlannedBroadcastStatus(
    startEventTimeMs: number | undefined,
    broadcastStatus: BroadcastStatus | undefined
): PlannedBroadcastStatus | undefined {
    if (
        typeof startEventTimeMs !== 'number' ||
        broadcastStatus === undefined ||
        [BroadcastStatus.FINISHED, BroadcastStatus.PAUSED].includes(broadcastStatus)
    ) {
        return undefined;
    }

    const countdownTime = startEventTimeMs - Date.now();
    const effective = Math.max(0, countdownTime);

    if (effective > Time.HOUR) {
        return PlannedBroadcastStatus.DATE;
    }
    if (effective === 0) {
        return PlannedBroadcastStatus.NOW;
    }
    return PlannedBroadcastStatus.HOUR;
}

const getPlannedText = (status: PlannedBroadcastStatus) => {
    switch (status) {
        case PlannedBroadcastStatus.DATE:
            return i18n('broadcast-view', 'broadcast-start-date-title');
        case PlannedBroadcastStatus.HOUR:
            return i18n('broadcast-view', 'broadcast-start-countdown');
        case PlannedBroadcastStatus.NOW:
            return i18n('broadcast-view', 'broadcast-start-countdown-ended');
    }
};

const PlayerStub = () => {
    const { caption, startEventTime } = useSelector(getBroadcastLiveData);
    const status = useSelector(getBroadcastViewStatus);
    const shouldRenderCover = useSelector(isCoverLayerEnabled);
    const themeName = useSelector(getThemeName);
    const isMobile = useSelector(isMobileUa);

    const plannedPhaseFromSchedule = useMemo(
        () => computePlannedBroadcastStatus(startEventTime, status),
        [startEventTime, status]
    );

    const [plannedStatus, setPlannedStatus] = useState<PlannedBroadcastStatus | undefined>(plannedPhaseFromSchedule);

    useEffect(() => {
        setPlannedStatus(plannedPhaseFromSchedule);
    }, [plannedPhaseFromSchedule]);

    const onCountdownEnd = useCallback(() => {
        setPlannedStatus(PlannedBroadcastStatus.NOW);
    }, []);

    const renderCountDownTimerRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        if (plannedStatus === PlannedBroadcastStatus.DATE && startEventTime) {
            const timeout = startEventTime - Date.now() - 3600000;

            if (timeout < 2147483647) {
                renderCountDownTimerRef.current = setTimeout(() => {
                    setPlannedStatus(PlannedBroadcastStatus.HOUR);
                }, timeout);
            }
        }

        return () => {
            if (renderCountDownTimerRef.current) {
                clearTimeout(renderCountDownTimerRef.current);
            }
        };
    }, [startEventTime, plannedStatus]);

    return (
        <div
            className={cn(
                classes.PlayerStub,
                themeName === ThemeName.GeneralDefault || isMobile ? 'ps-dark-transparent' : getPageClassName(themeName)
            )}
        >
            {shouldRenderCover ? <CoverLayer isAbsolute /> : <Background isAbsolute />}
            <div className={classes.PlayerStub__TitleWrapper}>
                <div
                    className={cn(classes.PlayerStub__Title, {
                        [classes.PlayerStub__Title_long]: caption && caption.length > 70,
                    })}
                >
                    {caption}
                </div>
                {[BroadcastStatus.FINISHED, BroadcastStatus.PAUSED].includes(status) ? (
                    <div className={classes.PlayerStub__SubTitle}>{i18n('broadcast-view', 'broadcast-stopped')}</div>
                ) : null}
                {plannedStatus ? (
                    <div className={classes.PlayerStub__PlannedInfo}>
                        <div className={classes.PlayerStub__PlannedTitle}>{getPlannedText(plannedStatus)}</div>
                        <div
                            className={cn(classes.PlayerStub__PlannedDate, {
                                [classes.PlayerStub__PlannedDate_now]: plannedStatus === PlannedBroadcastStatus.NOW,
                            })}
                        >
                            {plannedStatus === PlannedBroadcastStatus.NOW ||
                            plannedStatus === PlannedBroadcastStatus.HOUR ? (
                                <Countdown onEnd={onCountdownEnd} />
                            ) : (
                                i18n('broadcast-view', 'broadcast-start-date', {
                                    date: formatDate(
                                        startEventTime as number,
                                        'DD mmmm YYYY',
                                        [],
                                        i18nMonthsGenitive()
                                    ),
                                    time: formatDate(startEventTime as number, 'HH:mm'),
                                })
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
            <BackgroundAudioButton
                autoplay={plannedStatus !== PlannedBroadcastStatus.DATE}
                className={classes.PlayerStub__AudioButton}
            />
        </div>
    );
};

export default PlayerStub;
